import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
  IconButton
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { setTutEditMode } from "../../../redux/slices/tutorialEditSlice";
import { toast } from "react-toastify";

import MachineListAPIs from "../../../utilities/api/machine/AdminMachineList";
import EquipmentListAPIs from "../../../utilities/api/equipment/AdminEquipmentList";
import InventoryListAPIs from "../../../utilities/api/inventory/AdminInventoryList";
import AdminModuleAPIs from "../../../utilities/api/tutorials/AdminModuleAPIs";
import AdminVideoAPIs from "../../../utilities/api/tutorials/AdminVideoAPIs";

const EditModuleForm = () => {
  const dispatch = useDispatch();
  const currentItem = useSelector(
    state => state.tutorialEdit.tutCurrentItem
  );
  const user = useSelector(state => state.user.user);

  const [machines, setMachines] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [inventories, setInventories] = useState([]);
  const [videos, setVideos] = useState([]);
  const [image, setImage] = useState(null);
  const [document, setDocument] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [existingDocument, setExistingDocument] = useState(null);
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required("Module name required"),
    description: Yup.string().required("Description required")
  });

  const isValidURL = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};


  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      related_machines: [],
      related_equipments: [],
      related_inventory: []
    },
    validationSchema,
    enableReinitialize: true,

    onSubmit: async values => {
      setLoading(true);

      try {
        /* ===========================
           1️⃣ Update/Create Videos
        =========================== */

        const updatedVideos = [];

        for (let i = 0; i < videos.length; i++) {
          const v = videos[i];

/* ===========================
   🔥 Video Validations
=========================== */

const links = videos.map(v => v.link.trim());

//  Empty fields
for (let i = 0; i < videos.length; i++) {
  const v = videos[i];

  if (!v.title.trim() || !v.link.trim() || !v.length) {
    toast.error(`Video ${i + 1}: All fields are required.`);
    setLoading(false);
    return;
  }
}

//  Invalid URL
for (let i = 0; i < links.length; i++) {
  if (!isValidURL(links[i])) {
    toast.error(`Video ${i + 1}: Invalid URL format.`);
    setLoading(false);
    return;
  }
}

//  Duplicate Links
const uniqueLinks = new Set(links);

if (uniqueLinks.size !== links.length) {
  toast.error("Duplicate video links are not allowed.");
  setLoading(false);
  return;
}


          const totalMinutes = parseInt(v.length);
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;

          const formattedLength = `${String(hours).padStart(
            2,
            "0"
          )}:${String(minutes).padStart(2, "0")}:00`;

          let videoId = v.id;

          if (videoId) {
            await AdminVideoAPIs.UpdateVideo(videoId, {
              title: v.title,
              link: v.link,
              video_length: formattedLength
            });
          } else {
            const created = await AdminVideoAPIs.CreateVideoPost({
              title: v.title,
              link: v.link,
              video_length: formattedLength
            });
            videoId = created.id;
          }

          updatedVideos.push({
            video_id: videoId,
            order: i + 1
          });
        }

        /* ===========================
            Update Module (JSON)
        =========================== */

        const payload = {
          name: values.name,
          description: values.description,
          related_machines: values.related_machines.map(m => m.id),
          related_equipments: values.related_equipments.map(e => e.id),
          related_inventory: values.related_inventory.map(i => i.id),
          videos: updatedVideos
        };

        await AdminModuleAPIs.UpdateModule(
          currentItem.id,
          payload
        );

        /* ===========================
           3 Upload Files Separately
        =========================== */

        if (image || document) {
          const fileData = new FormData();

          if (image) fileData.append("image", image);
          if (document) fileData.append("document", document);

          await AdminModuleAPIs.UpdateModule(
            currentItem.id,
            fileData
          );
        }

        toast.success("Module updated successfully");
        dispatch(setTutEditMode(false));

      } catch (err) {
        console.error("Update error:", err.response?.data || err);
        toast.error("Failed to update module");
      }

      setLoading(false);
    }
  });

  /* ===========================
     Load Dropdown Data
  =========================== */

  useEffect(() => {
    const load = async () => {
      const [m, e, i] = await Promise.all([
        MachineListAPIs.MachineListGet(),
        EquipmentListAPIs.EquipmentListGet(),
        InventoryListAPIs.InventoryListGet({
          refresh_token: user.refresh,
          access_token: user.access
        })
      ]);

      setMachines(m || []);
      setEquipments(e || []);
      setInventories(i || []);
    };

    if (user) load();
  }, [user]);

  /* ===========================
     Load Module Details
  =========================== */

  useEffect(() => {
    const loadModule = async () => {
      const fullModule =
        await AdminModuleAPIs.ModuleDetailGet(
          currentItem.id
        );

      setExistingImage(fullModule.image);
      setExistingDocument(fullModule.document);

      if (fullModule.videos?.length) {
        const mapped = fullModule.videos.map(item => {
          const video = item.video;

          const [h, m] =
            video.video_length?.split(":") || [0, 0];

          return {
            id: video.id,
            title: video.title,
            link: video.link,
            length: parseInt(h) * 60 + parseInt(m)
          };
        });

        setVideos(mapped);
      }

      formik.setValues({
        name: fullModule.name || "",
        description: fullModule.description || "",
        related_machines: machines.filter(m =>
          fullModule.related_machines?.includes(m.id)
        ),
        related_equipments: equipments.filter(e =>
          fullModule.related_equipments?.includes(e.id)
        ),
        related_inventory: inventories.filter(i =>
          fullModule.related_inventory?.includes(i.id)
        )
      });
    };

    if (currentItem?.id && machines.length) {
      loadModule();
    }
  }, [currentItem, machines, equipments, inventories]);

  /* ===========================
     Video Handlers
  =========================== */

  const addVideo = () =>
    setVideos(prev => [
      ...prev,
      { title: "", link: "", length: "" }
    ]);

  const removeVideo = index =>
    setVideos(prev =>
      prev.filter((_, i) => i !== index)
    );

  const updateVideo = (index, field, value) => {
    setVideos(prev => {
      const copy = [...prev];
      copy[index][field] = value;
      return copy;
    });
  };

  if (!currentItem)
    return <Typography p={3}>No module selected</Typography>;

  /* ===========================
     UI
  =========================== */

  return (
    <Box component="form" onSubmit={formik.handleSubmit} p={3}>
      <Box display="flex" alignItems="center" mb={2}>
        <IconButton
          onClick={() => dispatch(setTutEditMode(false))}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={600}>
          Edit Module
        </Typography>
      </Box>

      <TextField
        label="Module Name"
        name="name"
        fullWidth
        value={formik.values.name}
        onChange={formik.handleChange}
        sx={{ mb: 2 }}
      />

      <TextField
        label="Description"
        name="description"
        multiline
        rows={4}
        fullWidth
        value={formik.values.description}
        onChange={formik.handleChange}
        sx={{ mb: 3 }}
      />

      {/* Document */}
      <Typography fontWeight={600}>Module Document</Typography>

      <Button variant="outlined" component="label" sx={{ mb: 2 }}>
        Upload Document
        <input
          hidden
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={e => setDocument(e.target.files[0])}
        />
      </Button>

      {document && (
        <Typography mb={2}>
          Selected: {document.name}
        </Typography>
      )}

      {!document && existingDocument && (
        <Typography mb={2}>
          Current: {existingDocument.split("/").pop()}
        </Typography>
      )}

      {/* Image */}


      {image && (
        <Typography mb={2}>
          Selected: {image.name}
        </Typography>
      )}

      {!image && existingImage && (
        <img
          src={`${process.env.REACT_APP_API_BASE_URL}${existingImage}`}
          alt="Module"
          width="200"
          style={{ borderRadius: 8 }}
        />
      )}
      <Typography fontWeight={600}>Module Image</Typography>

      <Button variant="outlined" component="label" sx={{ mb: 5}}>
        Upload Image
        <input
          hidden
          type="file"
          accept="image/*"
          onChange={e => setImage(e.target.files[0])}
        />
      </Button>
      {/* Videos */}
      <Typography fontWeight={600} mb={2}>
        Videos
      </Typography>

      {videos.map((v, i) => (
        <Box key={i} mb={2}>
          <TextField
            label="Title"
            fullWidth
            value={v.title}
            onChange={e =>
              updateVideo(i, "title", e.target.value)
            }
            sx={{ mb: 1 }}
          />
          <TextField
            label="Link"
            fullWidth
            value={v.link}
            onChange={e =>
              updateVideo(i, "link", e.target.value)
            }
            sx={{ mb: 1 }}
          />
          <TextField
            label="Length (minutes)"
            type="number"
            fullWidth
            value={v.length}
            onChange={e =>
              updateVideo(i, "length", e.target.value)
            }
          />
          <IconButton
            color="error"
            onClick={() => removeVideo(i)}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={addVideo}
        sx={{ mb: 3 }}
      >
        Add Video
      </Button>

      <Box display="flex" gap={2}>
        <Button
          variant="outlined"
          onClick={() => dispatch(setTutEditMode(false))}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          variant="contained"
          disabled={loading}
        >
          {loading ? "Updating..." : "Update"}
        </Button>
      </Box>
    </Box>
  );
};

export default EditModuleForm;
